import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Baby, School, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    parentName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    scheduledTour: '',
    preferredContact: '',
    emergencyName: '',
    emergencyPhone: '',
    emergencyRelation: '',
    emergency2Name: '',
    emergency2Phone: '',
    emergency2Relation: '',
    childName: '',
    childAge: '',
    ageGroup: '',
    startDate: '',
    nickname: '',
    favoriteFood: '',
    favoriteToy: '',
    toyName: '',
    hasAllergies: '',
    allergies: '',
    message: '',
    tourDate: '',
    tourTime: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/enrollments`, {
        parent_name: formData.parentName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        scheduled_tour: formData.scheduledTour,
        preferred_contact: formData.preferredContact,
        child_name: formData.childName,
        child_age: parseInt(formData.childAge),
        age_group: formData.ageGroup,
        start_date: formData.startDate,
        nickname: formData.nickname,
        favorite_food: formData.favoriteFood,
        favorite_toy: formData.favoriteToy,
        toy_name: formData.toyName,
        has_allergies: formData.hasAllergies,
        allergies: formData.allergies,
        emergency_name: formData.emergencyName,
        emergency_phone: formData.emergencyPhone,
        emergency_relation: formData.emergencyRelation,
        emergency2_name: formData.emergency2Name,
        emergency2_phone: formData.emergency2Phone,
        emergency2_relation: formData.emergency2Relation,
        message: formData.message,
      });
      toast.success('Registration Submitted!', { description: 'Thank you for registering! We will contact you shortly.' });

      // Also submit tour request if they filled in tour details
      if (formData.scheduledTour === 'no' && formData.tourDate && formData.tourTime) {
        try {
          await axios.post(`${API}/tour-requests`, {
            parent_name: formData.parentName,
            email: formData.email,
            phone: formData.phone,
            preferred_date: formData.tourDate,
            preferred_time: formData.tourTime,
            num_children: 1,
            child_ages: String(formData.childAge),
            message: `Tour scheduled during enrollment for ${formData.childName}`,
          });
          toast.success('Tour also scheduled!', { description: `Tour requested for ${formData.tourDate} at ${formData.tourTime}` });
        } catch { /* tour submit failed silently */ }
      }

      setFormData({ parentName: '', email: '', phone: '', address: '', city: '', state: '', zip: '', scheduledTour: '', preferredContact: '', emergencyName: '', emergencyPhone: '', emergencyRelation: '', emergency2Name: '', emergency2Phone: '', emergency2Relation: '', childName: '', childAge: '', ageGroup: '', startDate: '', nickname: '', favoriteFood: '', favoriteToy: '', toyName: '', hasAllergies: '', allergies: '', message: '', tourDate: '', tourTime: '' });
      setTimeout(() => navigate('/'), 2000);
    } catch {
      toast.error('Something went wrong', { description: 'Please try again or call us directly.' });
    }
  };

  const stepIndicators = [
    { num: 1, label: 'Parent Info' },
    { num: 2, label: 'Child Info' },
  ];

  return (
    <div className="min-h-screen bg-background py-12 px-4" data-testid="register-page">
      <div className="container mx-auto max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2 rounded-full hover:bg-primary/8 text-foreground/70 hover:text-foreground"
            data-testid="register-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <Link to="/">
            <div className="font-fredoka font-bold text-lg">
              <span className="text-primary">Hoo</span>
              <span className="text-secondary">yo's</span>
              <span className="text-accent"> Place</span>
            </div>
          </Link>
        </div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-[2.5rem] shadow-elevated border border-border/40 overflow-hidden"
        >
          {/* Top Section */}
          <div className="bg-primary/5 p-8 text-center space-y-4 border-b border-border/30">
            <div className="flex justify-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/12 flex items-center justify-center">
                <Baby className="w-7 h-7 text-primary" />
              </div>
              <div className="w-14 h-14 rounded-full bg-secondary/12 flex items-center justify-center">
                <School className="w-7 h-7 text-secondary" />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-fredoka font-bold text-foreground" data-testid="register-heading">
              Enrollment Registration
            </h1>
            <p className="text-muted-foreground text-sm md:text-base max-w-md mx-auto">
              Join the Hooyo's Place family! Fill out the form below and we'll be in touch shortly.
            </p>

            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-3 pt-2">
              {stepIndicators.map((s) => (
                <button
                  key={s.num}
                  onClick={() => setStep(s.num)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
                    step === s.num
                      ? 'bg-primary text-white shadow-soft'
                      : step > s.num
                      ? 'bg-success/15 text-success'
                      : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`register-step-${s.num}`}
                >
                  {step > s.num ? <CheckCircle2 className="w-4 h-4" /> : <span className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-xs">{s.num}</span>}
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: -15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-5"
              >
                <h3 className="text-lg font-fredoka font-semibold text-foreground">Parent/Guardian Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="parentName">Full Name *</Label>
                    <Input id="parentName" name="parentName" placeholder="Jane Doe" required value={formData.parentName} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-parent-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input id="phone" name="phone" type="tel" placeholder="(503) 555-1234" required value={formData.phone} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-phone" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input id="email" name="email" type="email" placeholder="parent@example.com" required value={formData.email} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address *</Label>
                  <Input id="address" name="address" placeholder="123 Main St" required value={formData.address} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-address" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input id="city" name="city" placeholder="Beaverton" required value={formData.city} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-city" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Input id="state" name="state" placeholder="OR" required value={formData.state} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-state" />
                  </div>
                  <div className="space-y-2 col-span-2 md:col-span-1">
                    <Label htmlFor="zip">Zip Code *</Label>
                    <Input id="zip" name="zip" placeholder="97006" required value={formData.zip} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-zip" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Did you schedule a tour? *</Label>
                    <Select value={formData.scheduledTour} onValueChange={(value) => setFormData((prev) => ({ ...prev, scheduledTour: value }))}>
                      <SelectTrigger className="border-2 border-border/60 focus:border-primary rounded-2xl h-12" data-testid="register-tour-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Contact Method *</Label>
                    <Select value={formData.preferredContact} onValueChange={(value) => setFormData((prev) => ({ ...prev, preferredContact: value }))}>
                      <SelectTrigger className="border-2 border-border/60 focus:border-primary rounded-2xl h-12" data-testid="register-contact-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Emergency Contact */}
                <div className="border-t border-border/30 pt-5">
                  <h3 className="text-lg font-fredoka font-semibold text-foreground mb-4">Emergency Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="emergencyName">Contact Name *</Label>
                      <Input id="emergencyName" name="emergencyName" placeholder="John Doe" required value={formData.emergencyName} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-emergency-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergencyPhone">Contact Phone *</Label>
                      <Input id="emergencyPhone" name="emergencyPhone" type="tel" placeholder="(503) 555-6789" required value={formData.emergencyPhone} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-emergency-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergencyRelation">Relationship *</Label>
                      <Input id="emergencyRelation" name="emergencyRelation" placeholder="e.g. Grandparent, Uncle" required value={formData.emergencyRelation} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-emergency-relation" />
                    </div>
                  </div>
                </div>
                {/* Emergency Contact 2 */}
                <div className="border-t border-border/30 pt-5">
                  <h3 className="text-lg font-fredoka font-semibold text-foreground mb-4">Emergency Contact 2</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="emergency2Name">Contact Name</Label>
                      <Input id="emergency2Name" name="emergency2Name" placeholder="Jane Smith" value={formData.emergency2Name} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-emergency2-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergency2Phone">Contact Phone</Label>
                      <Input id="emergency2Phone" name="emergency2Phone" type="tel" placeholder="(503) 555-9876" value={formData.emergency2Phone} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-emergency2-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emergency2Relation">Relationship</Label>
                      <Input id="emergency2Relation" name="emergency2Relation" placeholder="e.g. Aunt, Neighbor" value={formData.emergency2Relation} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-emergency2-relation" />
                    </div>
                  </div>
                </div>
                {formData.scheduledTour === 'no' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.25 }}
                    className="bg-accent/8 rounded-2xl p-5 border border-accent/20 space-y-4"
                    data-testid="register-tour-prompt"
                  >
                    <p className="text-sm font-semibold text-foreground">Would you like to schedule a tour while you're here?</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="tourDate">Preferred Tour Date</Label>
                        <Input id="tourDate" name="tourDate" type="date" value={formData.tourDate || ''} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12 bg-white" data-testid="register-tour-date" />
                      </div>
                      <div className="space-y-2">
                        <Label>Preferred Tour Time</Label>
                        <Select value={formData.tourTime || ''} onValueChange={(value) => setFormData((prev) => ({ ...prev, tourTime: value }))}>
                          <SelectTrigger className="border-2 border-border/60 focus:border-primary rounded-2xl h-12 bg-white" data-testid="register-tour-time">
                            <SelectValue placeholder="Select a time" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="9:00 AM">9:00 AM</SelectItem>
                            <SelectItem value="10:00 AM">10:00 AM</SelectItem>
                            <SelectItem value="11:00 AM">11:00 AM</SelectItem>
                            <SelectItem value="12:00 PM">12:00 PM</SelectItem>
                            <SelectItem value="1:00 PM">1:00 PM</SelectItem>
                            <SelectItem value="2:00 PM">2:00 PM</SelectItem>
                            <SelectItem value="3:00 PM">3:00 PM</SelectItem>
                            <SelectItem value="4:00 PM">4:00 PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Leave blank if you'd like to schedule later.</p>
                  </motion.div>
                )}
                <div className="flex justify-end pt-2">
                  <Button type="button" onClick={() => setStep(2)} className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-5 font-bold shadow-soft hover:shadow-elevated transition-all hover:scale-105 active:scale-95" data-testid="register-next-btn">
                    Next Step
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 15 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-5"
              >
                <h3 className="text-lg font-fredoka font-semibold text-foreground">Child Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="childName">Child's Name *</Label>
                    <Input id="childName" name="childName" placeholder="Emma" required value={formData.childName} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-child-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="childAge">Child's Age *</Label>
                    <Input id="childAge" name="childAge" type="number" min="1" max="10" placeholder="4" required value={formData.childAge} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-child-age" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nickname">Nickname to be called?</Label>
                    <Input id="nickname" name="nickname" placeholder="e.g. Emmy" value={formData.nickname} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-nickname" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="favoriteFood">Favorite Food?</Label>
                    <Input id="favoriteFood" name="favoriteFood" placeholder="e.g. Mac & Cheese" value={formData.favoriteFood} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-favorite-food" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="favoriteToy">Favorite Toy?</Label>
                    <Input id="favoriteToy" name="favoriteToy" placeholder="e.g. Teddy Bear" value={formData.favoriteToy} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-favorite-toy" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="toyName">Does it have a name?</Label>
                    <Input id="toyName" name="toyName" placeholder="e.g. Mr. Snuggles" value={formData.toyName} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-toy-name" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ageGroup">Program Selection *</Label>
                    <Select value={formData.ageGroup} onValueChange={(value) => setFormData((prev) => ({ ...prev, ageGroup: value }))}>
                      <SelectTrigger className="border-2 border-border/60 focus:border-primary rounded-2xl h-12" data-testid="register-program-select">
                        <SelectValue placeholder="Select a program" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-5-morning">Ages 1-5 (Morning 7AM-3PM)</SelectItem>
                        <SelectItem value="6-10-afternoon">Ages 6-10 (Afternoon 3PM-8PM)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Preferred Start Date *</Label>
                    <Input id="startDate" name="startDate" type="date" required value={formData.startDate} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-start-date" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Allergies? *</Label>
                    <Select value={formData.hasAllergies} onValueChange={(value) => setFormData((prev) => ({ ...prev, hasAllergies: value }))}>
                      <SelectTrigger className="border-2 border-border/60 focus:border-primary rounded-2xl h-12" data-testid="register-allergies-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.hasAllergies === 'yes' && (
                    <motion.div
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-2"
                    >
                      <Label htmlFor="allergies">Please list allergies *</Label>
                      <Input id="allergies" name="allergies" placeholder="e.g. Peanuts, Dairy, Eggs" required value={formData.allergies} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl h-12" data-testid="register-allergies-input" />
                    </motion.div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Tell us more about {formData.childName || 'your child'}</Label>
                  <Textarea id="message" name="message" placeholder={`Tell us anything else we should know about ${formData.childName || 'your child'} (special needs, fears, etc.)`} rows={3} value={formData.message} onChange={handleInputChange} className="border-2 border-border/60 focus-visible:border-primary rounded-2xl resize-none" data-testid="register-message" />
                </div>
                <div className="flex justify-between pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="rounded-full px-6 py-5 font-bold border-2 hover:bg-muted" data-testid="register-prev-btn">
                    Back
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-5 font-bold shadow-soft hover:shadow-elevated transition-all hover:scale-105 active:scale-95" data-testid="register-submit-btn">
                    Submit Registration
                  </Button>
                </div>
              </motion.div>
            )}
          </form>
        </motion.div>

        {/* Info Notice */}
        <div className="mt-8 text-center text-muted-foreground space-y-2">
          <p className="text-sm">After submitting, you'll receive a confirmation within 24 hours.</p>
          <p className="text-sm">
            Questions? Call us at <a href="tel:971-867-0536" className="font-bold text-primary hover:underline">971-867-0536</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
